import { type EEmailType, type IAddress } from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import {
  type ICrmCustomerDetailState,
  type ICustomerCouponCreate,
  type ICustomerDetail,
  type ICustomerDetailEmailPreview,
  type IFakturoidSyncResult,
} from './types';

export const preloadedState: ICrmCustomerDetailState = {
  ...asyncStateInitial,
  customer: undefined,
  fakturoidSyncInProcess: false,
  fakturoidSyncResult: undefined,
  emailResendInProcess: false,
  emailPreviewUUID: null,
  emailPreview: null,
  emailPreviewInProcess: false,
  couponCreateInProcess: false,
};

const crmCustomerDetailSlice = createSlice({
  name: 'CrmCustomerDetail',
  initialState: preloadedState,
  reducers: {
    crmCustomerDetailLoad: {
      reducer(state) {
        startLoading(state);
        state.customer = undefined;
      },
      prepare(uuid: string) {
        return { payload: { uuid } };
      },
    },
    crmCustomerDetailLoadResponse: {
      reducer(
        state,
        action: { payload: { customer: ICustomerDetail | null } },
      ) {
        state.customer = action.payload.customer;
        state.emailResendInProcess = false;
        state.couponCreateInProcess = false;
        finishLoading(state);
      },
      prepare(customer: ICustomerDetail | null) {
        return { payload: { customer } };
      },
    },
    crmCustomerFakturoidSync: {
      reducer(state) {
        state.fakturoidSyncInProcess = true;
        state.fakturoidSyncResult = undefined;
      },
      prepare(customerUUID: string) {
        return { payload: { customerUUID } };
      },
    },
    crmCustomerFakturoidSyncResponse: {
      reducer(state, action: { payload: { result: IFakturoidSyncResult } }) {
        state.fakturoidSyncInProcess = false;
        state.fakturoidSyncResult = action.payload.result;
        if (
          action.payload.result.success &&
          action.payload.result.fakturoidId != null &&
          state.customer != null
        ) {
          state.customer.fakturoidId = action.payload.result.fakturoidId;
        }
      },
      prepare(result: IFakturoidSyncResult) {
        return { payload: { result } };
      },
    },
    crmCustomerEmailResend: {
      reducer(state) {
        state.emailResendInProcess = true;
      },
      prepare(
        customerUUID: string,
        emailTypeCode: EEmailType,
        orderUUID: string,
      ) {
        return {
          payload: {
            customer: { uuid: customerUUID },
            emailTypeCode,
            orderUUID,
          },
        };
      },
    },
    crmCustomerEmailPreviewLoad: {
      reducer(state, action: { payload: { uuid: string } }) {
        state.emailPreviewUUID = action.payload.uuid;
        state.emailPreview = null;
        state.emailPreviewInProcess = true;
      },
      prepare(uuid: string) {
        return { payload: { uuid } };
      },
    },
    crmCustomerEmailPreviewLoadResponse: {
      reducer(
        state,
        action: { payload: { preview: ICustomerDetailEmailPreview | null } },
      ) {
        state.emailPreview = action.payload.preview;
        state.emailPreviewInProcess = false;
      },
      prepare(preview: ICustomerDetailEmailPreview | null) {
        return { payload: { preview } };
      },
    },
    crmCustomerEmailPreviewClose(state) {
      state.emailPreviewUUID = null;
      state.emailPreview = null;
      state.emailPreviewInProcess = false;
    },
    crmCustomerAddressUpdate: {
      reducer(state) {
        startLoading(state);
      },
      prepare(customerUUID: string, addressUUID: string, address: IAddress) {
        return {
          payload: { customer: { uuid: customerUUID }, addressUUID, address },
        };
      },
    },
    crmCustomerCouponCreate: {
      reducer(state) {
        state.couponCreateInProcess = true;
      },
      prepare(customerUUID: string, coupon: ICustomerCouponCreate) {
        return { payload: { customer: { uuid: customerUUID }, coupon } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(requestFailedMatcher('CrmCustomerDetail'), (state) => {
      resetLoadingOnRequestFailed(state);
      state.emailResendInProcess = false;
      state.emailPreviewInProcess = false;
      state.couponCreateInProcess = false;
    });
  },
});

export const {
  crmCustomerDetailLoad,
  crmCustomerDetailLoadResponse,
  crmCustomerFakturoidSync,
  crmCustomerFakturoidSyncResponse,
  crmCustomerAddressUpdate,
  crmCustomerCouponCreate,
  crmCustomerEmailResend,
  crmCustomerEmailPreviewLoad,
  crmCustomerEmailPreviewLoadResponse,
  crmCustomerEmailPreviewClose,
} = crmCustomerDetailSlice.actions;
export default crmCustomerDetailSlice.reducer;
